/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: NetworkSettingFormView.tsx
 ??? Created: 2025-12-04
 ?? Description:
    View and edit network configuration for a specific telephony server
----------------------------------------------------------------*/

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Network, Server, Save } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { fetchServerNetworkConfig, updateServerHostname } from './api/networkSettingApi';
import type { ServerNetworkConfig, NetworkInterface } from './types';

export default function NetworkSettingFormView() {
  const { id: serverId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showError, showSuccess } = useNotifications();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverConfig, setServerConfig] = useState<ServerNetworkConfig | null>(null);
  const [hostname, setHostname] = useState<string>('');

  // Load server network configuration
  useEffect(() => {
    if (serverId) {
      loadServerConfig();
    }
  }, [serverId]);

  const loadServerConfig = async () => {
    if (!serverId) return;

    try {
      setLoading(true);
      const config = await fetchServerNetworkConfig(serverId);
      setServerConfig(config);
      
      // Set hostname from first interface
      const firstInterface = config.interfaces[0];
      if (firstInterface?.hostname) {
        setHostname(firstInterface.hostname);
      }
    } catch (error) {
      showError(error.message || t('networkSettings.messages.loadError'));
      navigate('/admin/network/settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHostname = async () => {
    if (!serverId || !hostname.trim()) {
      showError(t('networkSettings.messages.hostnameRequired'));
      return;
    }

    try {
      setSaving(true);
      await updateServerHostname(serverId, hostname.trim());
      
      showSuccess(t('networkSettings.messages.hostnameSaved'));
      // Reload configuration to get updated values
      await loadServerConfig();
    } catch (error) {
      showError(error.message || t('networkSettings.messages.hostnameSaveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (!serverConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('networkSettings.messages.loadError')}</div>
      </div>
    );
  }

  const getStateColor = (state: string) => {
    switch (state) {
      case 'UP':
        return 'text-green-600 dark:text-green-400';
      case 'DOWN':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="p-6 pb-6">
      <ModuleFormHeader
        icon={<Network className="h-5 w-5" />}
        title={`${t('networkSettings.viewTitle')} - ${serverConfig.serverName}`}
        backButtonText={t('networkSettings.actions.backToList')}
        onBack={() => navigate('/admin/network/settings')}
      />

      {/* Hostname Configuration */}
      <div className="mt-8">
        <FormBox title={t('networkSettings.hostnameSection.title')} icon={<Server className="h-4 w-4" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 items-center">
            <Label className="text-left">{t('networkSettings.fields.hostname')}</Label>
            <div className="col-span-2">
              <Input 
                value={hostname} 
                onChange={(e) => setHostname(e.target.value)}
                className="font-mono"
                placeholder="server-hostname"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleSaveHostname}
                disabled={saving || !hostname.trim()}
                className="inline-flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </FormBox>
      </div>

      {/* Network Interfaces */}
      <div className="mt-8 space-y-8">
        {serverConfig.interfaces.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            {t('networkSettings.noInterfacesFound')}
          </div>
        ) : (
          serverConfig.interfaces.map((iface: NetworkInterface, index: number) => (
            <div key={iface.interfaceName} className="space-y-4">
              {/* Interface Title */}
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Network className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">{iface.interfaceName}</h3>
                <span className={`ml-auto font-semibold ${getStateColor(iface.state)}`}>
                  {iface.state}
                </span>
              </div>

              {/* IP Configuration */}
              <FormBox title="IP Configuration" icon={<Network className="h-4 w-4" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 items-center">
                  {/* IP Address & Netmask */}
                  <Label className="text-left">{t('networkSettings.fields.ipAddress')}</Label>
                  <Input value={iface.ipAddress} disabled className="font-mono bg-muted" />

                  <Label className="text-left">{t('networkSettings.fields.netmask')}</Label>
                  <Input value={`${iface.netmask} (/${iface.cidr})`} disabled className="font-mono bg-muted" />

                  {/* Gateway & DHCP */}
                  <Label className="text-left">{t('networkSettings.fields.gateway')}</Label>
                  <Input value={iface.gateway} disabled className="font-mono bg-muted" />

                  <Label className="text-left">{t('networkSettings.fields.dhcpEnabled')}</Label>
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                      iface.dhcpEnabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {iface.dhcpEnabled ? t('networkSettings.dhcpEnabled') : t('networkSettings.dhcpDisabled')}
                    </span>
                  </div>
                </div>
              </FormBox>

              {/* DNS Configuration */}
              <FormBox title="DNS Configuration" icon={<Network className="h-4 w-4" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 items-center">
                  <Label className="text-left">{t('networkSettings.fields.primaryDns')}</Label>
                  <Input value={iface.primaryDns} disabled className="font-mono bg-muted" />

                  {iface.secondaryDns && (
                    <>
                      <Label className="text-left">{t('networkSettings.fields.secondaryDns')}</Label>
                      <Input value={iface.secondaryDns} disabled className="font-mono bg-muted" />
                    </>
                  )}
                </div>
              </FormBox>

              {/* Hardware & Advanced */}
              <FormBox title="Hardware & Advanced" icon={<Network className="h-4 w-4" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 items-center">
                  {/* MAC Address & MTU */}
                  <Label className="text-left">MAC Address</Label>
                  <Input value={iface.mac || 'N/A'} disabled className="font-mono bg-muted" />

                  <Label className="text-left">{t('networkSettings.fields.mtu')}</Label>
                  <Input value={iface.mtu} disabled className="font-mono bg-muted" />

                  {/* Speed (if available) */}
                  {iface.speed && (
                    <>
                      <Label className="text-left">Speed</Label>
                      <Input value={iface.speed} disabled className="font-mono bg-muted" />
                      <div></div>
                      <div></div>
                    </>
                  )}
                </div>
              </FormBox>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
